import React from "react";
import moment from "moment";
import Input from "../../../components/input";
import FieldTooltip from "./FieldTooltip";

const selectOptions = (copy, options = []) => [
  { id: "", value: copy.form.select_placeholder },
].concat(options.map((option) => (typeof option === "string" ? { id: option, value: option } : option)));

const holderSelectOptions = (copy, options = []) => selectOptions(copy, options).concat({
  id: "__other_holder__",
  value: copy.form.holder_other_option || "Other",
});

const otherOptionValue = (field) => (field.options || []).find((option) => option === "Other" || option === "Otro");

const parseChangeValue = (field, value) => {
  if (field.type === "select" || field.type === "holder_select") return value.target.value;
  if (field.type === "number" || field.type === "currency" || field.type === "percentage") {
    if (field.maxValue !== undefined && value.floatValue > field.maxValue) return String(field.maxValue);
    return value.value;
  }
  if (field.type === "date") return value ? moment(value).format("MM/DD/YYYY") : "";
  return value.target.value;
};

const sameLinkedRecord = (record, option) => record && record.type === option.type && record.id === option.id;

const toPersistedLinkedRecord = (option) => ({ type: option.type, id: option.id, value: option.value });

const manualRecordId = (type, name) => `manual_${type}_${String(name || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")}`;

const linkedRecordLabel = (record, options) => {
  if (record && record.manual && record.value) return record.value;
  const option = options.find((item) => sameLinkedRecord(record, item));
  return option ? option.value : record.value || `${record.type || ""}${record.id ? ` - ${record.id}` : ""}`;
};

const manualOptionLabel = (field, copy) => (
  copy.form[field.manualOptionCopyKey] || copy.form.holder_other_option || "Other"
);

const FieldControl = ({ copy, currencyType, error, field, hidden = false, holderOptions = [], linkedOptions = [], value, onBlur, onChange }) => {
  const [assetSectionId, setAssetSectionId] = React.useState("");
  const [linkedSelectionKey, setLinkedSelectionKey] = React.useState(0);
  const otherValue = otherOptionValue(field);
  const isCustomOtherValue = field.otherEditable && value && otherValue && !field.options.includes(value);
  const showOtherInput = field.otherEditable && otherValue && (value === otherValue || isCustomOtherValue);
  const isCustomHolderValue = field.type === "holder_select" && value && value !== "__other_holder__" && !holderOptions.includes(value);
  const showHolderInput = field.type === "holder_select" && (value === "__other_holder__" || isCustomHolderValue);
  const selectedLinkedRecords = Array.isArray(value) ? value : [];
  const removeLinkedRecord = (record) => onChange(field.key, selectedLinkedRecords.filter((currentRecord) => !sameLinkedRecord(currentRecord, record)));

  if (field.type === "asset_link_selector") {
    const assetSections = linkedOptions.reduce((result, option) => (
      result.some((section) => section.id === option.sectionId)
        ? result
        : result.concat({ id: option.sectionId, value: option.sectionTitle })
    ), []);
    const availableAssets = linkedOptions.filter((option) => option.sectionId === assetSectionId);
    const addAsset = (event) => {
      const option = availableAssets.find((item) => `${item.type}:${item.id}` === event.target.value);
      if (option && !selectedLinkedRecords.some((record) => sameLinkedRecord(record, option))) {
        onChange(field.key, selectedLinkedRecords.concat(toPersistedLinkedRecord(option)));
      }
    };

    return (
      <div className="estate-mapping__field estate-mapping__field--linked wrapper-form-item" style={hidden ? { display: "none" } : undefined}>
        <span className="input-label">
          {field.label}
          <FieldTooltip content={field.tooltip} fieldId={field.key} label={copy.form.tooltip_label} />
        </span>
        <div className="estate-mapping__asset-link-selector">
          <Input
            colorGrey={true}
            defaultValue={assetSectionId}
            id={`${field.key}_section`}
            name={`${field.key}_section`}
            onChangeInput={(event) => setAssetSectionId(event.target.value)}
            options={[{ id: "", value: copy.form.choose_asset_type }].concat(assetSections)}
            type="select"
          />
          {assetSectionId && (
            <Input
              colorGrey={true}
              defaultValue=""
              id={`${field.key}_asset`}
              key={assetSectionId}
              name={`${field.key}_asset`}
              onChangeInput={addAsset}
              options={[{ id: "", value: copy.form.select_asset }].concat(availableAssets.map((option) => ({ id: `${option.type}:${option.id}`, value: option.value })))}
              type="select"
            />
          )}
          {selectedLinkedRecords.length > 0 && (
            <div className="estate-mapping__linked-summary">
              {selectedLinkedRecords.map((record) => (
                <span key={`${record.type}-${record.id}`}>
                  {linkedRecordLabel(record, linkedOptions)}
                  <button aria-label={`${copy.buttons.delete} ${record.value}`} onClick={() => removeLinkedRecord(record)} type="button">×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const toggleLinkedRecord = (option) => {
    const isSelected = selectedLinkedRecords.some((record) => sameLinkedRecord(record, option));
    const nextValue = isSelected
      ? selectedLinkedRecords.filter((record) => !sameLinkedRecord(record, option))
      : selectedLinkedRecords.concat(toPersistedLinkedRecord(option));
    onChange(field.key, nextValue);
  };

  if (field.type === "linked_select" && field.multiple) {
    const isStructureSelector = field.linkedTarget === "structures";
    const isLiabilitySelector = field.linkedTarget === "liabilities";
    const manualRecordType = isStructureSelector ? "structures" : "liabilities";
    const allowsManualRecord = isStructureSelector || isLiabilitySelector;
    const manualRecord = allowsManualRecord && selectedLinkedRecords.find((record) => (
      record && record.manual && record.type === manualRecordType
    ));
    const addLinkedRecord = (event) => {
      if (event.target.value === "__other_record__") {
        if (!manualRecord) {
          onChange(field.key, selectedLinkedRecords.concat({
            type: manualRecordType,
            id: "__other_record__",
            value: "",
            manual: true,
            ...(isStructureSelector ? { structureType: "Trust" } : {}),
          }));
        }
        setLinkedSelectionKey((current) => current + 1);
        return;
      }
      const option = linkedOptions.find((item) => `${item.type}:${item.id}` === event.target.value);
      if (option && !selectedLinkedRecords.some((record) => sameLinkedRecord(record, option))) {
        onChange(field.key, selectedLinkedRecords.concat(toPersistedLinkedRecord(option)));
      }
      setLinkedSelectionKey((current) => current + 1);
    };
    const updateManualRecord = (changes) => {
      const nextRecord = {
        ...manualRecord,
        ...changes,
      };
      if (changes.value !== undefined) nextRecord.id = manualRecordId(manualRecordType, changes.value) || "__other_record__";
      onChange(field.key, selectedLinkedRecords.map((record) => (
        record === manualRecord ? nextRecord : record
      )));
    };

    return (
      <div className="estate-mapping__field estate-mapping__field--linked wrapper-form-item" style={hidden ? { display: "none" } : undefined}>
        <span className="input-label">
          {field.label}
          {field.required && <span className="asterisk"> *</span>}
          <FieldTooltip content={field.tooltip} fieldId={field.key} label={copy.form.tooltip_label} />
        </span>
        <Input
          colorGrey={true}
          defaultValue=""
          disabled={linkedOptions.length === 0 && !allowsManualRecord}
          error={error}
          errorLabel={copy.form.required_error}
          id={field.key}
          key={`${field.key}-${linkedSelectionKey}`}
          name={field.key}
          onBlurInput={() => {}}
          onChangeInput={addLinkedRecord}
          options={[{ id: "", value: linkedOptions.length > 0 ? copy.form.select_placeholder : copy.form.no_linked_records }]
            .concat(linkedOptions.map((option) => ({ id: `${option.type}:${option.id}`, value: option.value })))
            .concat(allowsManualRecord ? [{ id: "__other_record__", value: manualOptionLabel(field, copy) }] : [])}
          required={field.required === true}
          showErrorLabel={error}
          showErrorLabelNow={error}
          type="select"
        />
        {manualRecord && (
          <div className={`estate-mapping__linked-manual${isStructureSelector ? "" : " estate-mapping__linked-manual--single"}`}>
            <Input
              colorGrey={true}
              defaultValue={manualRecord.value || ""}
              id={`${field.key}_other`}
              name={`${field.key}_other`}
              onChangeInput={(event) => updateManualRecord({ value: event.target.value })}
              placeholder={isStructureSelector ? copy.form.linked_structure_placeholder : copy.form.linked_liability_placeholder}
              type="text"
            />
            {isStructureSelector && (
              <Input
                colorGrey={true}
                defaultValue={manualRecord.structureType || "Trust"}
                id={`${field.key}_other_type`}
                name={`${field.key}_other_type`}
                onChangeInput={(event) => updateManualRecord({ structureType: event.target.value })}
                options={[
                  { id: "Trust", value: copy.option_values.Trust || "Trust" },
                  { id: "Corporation", value: copy.option_values.Corporation || "Corporation" },
                ]}
                type="select"
              />
            )}
          </div>
        )}
        {selectedLinkedRecords.length > 0 && (
          <div className="estate-mapping__linked-summary">
            {selectedLinkedRecords.map((record) => (
              <span key={`${record.type}-${record.id}`}>
                {linkedRecordLabel(record, linkedOptions)}
                <button aria-label={`${copy.buttons.delete} ${record.value}`} onClick={() => removeLinkedRecord(record)} type="button">×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (field.type === "linked_select") {
    const selectedRecord = selectedLinkedRecords[0];
    const isStructureSelector = field.linkedTarget === "structures";
    const isLiabilitySelector = field.linkedTarget === "liabilities";
    const manualRecordType = isStructureSelector ? "structures" : "liabilities";
    const allowsManualRecord = isStructureSelector || isLiabilitySelector;
    const isManualRecord = allowsManualRecord && selectedRecord && selectedRecord.manual;
    const selectedValue = isManualRecord ? "__other_record__" : selectedRecord ? `${selectedRecord.type}:${selectedRecord.id}` : "";
    const linkedSelectOptions = [{ id: "", value: linkedOptions.length > 0 ? copy.form.select_placeholder : copy.form.no_linked_records }]
      .concat(linkedOptions.map((option) => ({ id: `${option.type}:${option.id}`, value: option.value })))
      .concat(allowsManualRecord ? [{ id: "__other_record__", value: manualOptionLabel(field, copy) }] : []);
    const selectLinkedRecord = (event) => {
      if (event.target.value === "__other_record__") {
        onChange(field.key, [{
          type: manualRecordType,
          id: "__other_record__",
          value: "",
          manual: true,
          ...(isStructureSelector ? { structureType: "Trust" } : {}),
        }]);
        return;
      }
      const option = linkedOptions.find((item) => `${item.type}:${item.id}` === event.target.value);
      onChange(field.key, option ? [toPersistedLinkedRecord(option)] : []);
    };
    const updateManualStructure = (changes) => {
      const nextRecord = { ...selectedRecord, ...changes };
      if (changes.value !== undefined) nextRecord.id = manualRecordId(manualRecordType, changes.value) || "__other_record__";
      onChange(field.key, [nextRecord]);
    };

    return (
      <div className="estate-mapping__field estate-mapping__field--linked wrapper-form-item" style={hidden ? { display: "none" } : undefined}>
        <span className="input-label">
          {field.label}
          {field.required && <span className="asterisk"> *</span>}
          <FieldTooltip content={field.tooltip} fieldId={field.key} label={copy.form.tooltip_label} />
        </span>
        <Input
          colorGrey={true}
          defaultValue={selectedValue}
          disabled={linkedOptions.length === 0 && !allowsManualRecord}
          error={error}
          errorLabel={copy.form.required_error}
          id={field.key}
          name={field.key}
          onBlurInput={() => {}}
          onChangeInput={selectLinkedRecord}
          options={linkedSelectOptions}
          required={field.required === true}
          showErrorLabel={error}
          showErrorLabelNow={error}
          type="select"
        />
        {isManualRecord && (
          <div className={`estate-mapping__linked-manual${isStructureSelector ? "" : " estate-mapping__linked-manual--single"}`}>
            <Input
              colorGrey={true}
              id={`${field.key}_other`}
              name={`${field.key}_other`}
              onChangeInput={(event) => updateManualStructure({ value: event.target.value })}
              placeholder={isStructureSelector ? copy.form.linked_structure_placeholder : copy.form.linked_liability_placeholder}
              type="text"
              defaultValue={selectedRecord.value || ""}
            />
            {isStructureSelector && (
              <Input
                colorGrey={true}
                defaultValue={selectedRecord.structureType || "Trust"}
                id={`${field.key}_other_type`}
                name={`${field.key}_other_type`}
                onChangeInput={(event) => updateManualStructure({ structureType: event.target.value })}
                options={[
                  { id: "Trust", value: copy.option_values.Trust || "Trust" },
                  { id: "Corporation", value: copy.option_values.Corporation || "Corporation" },
                ]}
                type="select"
              />
            )}
          </div>
        )}
      </div>
    );
  }

  if (field.type === "linked_records") {
    return (
      <div className="estate-mapping__field estate-mapping__field--linked wrapper-form-item" style={hidden ? { display: "none" } : undefined}>
        <span className="input-label">
          {field.label}
          {field.required && <span className="asterisk"> *</span>}
        </span>
        <div className={`estate-mapping__linked-select${error ? " error" : ""}`}>
          {linkedOptions.length === 0 && <span className="estate-mapping__linked-empty">{copy.form.no_linked_records}</span>}
          {linkedOptions.map((option) => {
            const isSelected = selectedLinkedRecords.some((record) => sameLinkedRecord(record, option));
            return (
              <label className={`estate-mapping__linked-option${isSelected ? " is-selected" : ""}`} key={`${option.type}-${option.id}`}>
                <input checked={isSelected} onChange={() => toggleLinkedRecord(option)} type="checkbox" />
                <span>{option.value}</span>
              </label>
            );
          })}
        </div>
        {selectedLinkedRecords.length > 0 && (
          <div className="estate-mapping__linked-summary">
            {selectedLinkedRecords.map((record) => (
              <span key={`${record.type}-${record.id}`}>
                {linkedRecordLabel(record, linkedOptions)}
                {record.manual && <button aria-label={`${copy.buttons.delete} ${record.value}`} onClick={() => removeLinkedRecord(record)} type="button">×</button>}
              </span>
            ))}
          </div>
        )}
        {error && <span className="error-label">{copy.form.required_error}</span>}
      </div>
    );
  }

  return (
    <div className="estate-mapping__field wrapper-form-item" style={hidden ? { display: "none" } : undefined}>
      <span className="input-label">
        {field.label}
        {field.required && <span className="asterisk"> *</span>}
        <FieldTooltip content={field.tooltip} fieldId={field.key} label={copy.form.tooltip_label} />
      </span>
      <Input
        id={field.key}
        name={field.key}
        type={field.type === "holder_select" ? "select" : field.type}
        colorGrey={true}
        defaultValue={isCustomOtherValue ? otherValue : isCustomHolderValue ? "__other_holder__" : value || ""}
        placeholder={field.placeholder}
        options={field.type === "select" ? selectOptions(copy, field.options) : field.type === "holder_select" ? holderSelectOptions(copy, holderOptions) : undefined}
        currency_type={field.type === "currency" ? currencyType : undefined}
        maxValue={field.maxValue}
        allowFutureDate={field.allowFutureDate === true}
        prepare_dates={{ [field.key]: value || "" }}
        onChangeInput={(nextValue) => onChange(field.key, parseChangeValue(field, nextValue))}
        onBlurInput={() => onBlur && onBlur(field.key)}
        error={error}
        required={field.required === true}
        showErrorLabel={error}
        showErrorLabelNow={error}
        errorLabel={copy.form.required_error}
      />
      {showOtherInput && (
        <Input
          id={`${field.key}_other`}
          name={`${field.key}_other`}
          type="text"
          colorGrey={true}
          defaultValue={isCustomOtherValue ? value : ""}
          placeholder={field.otherPlaceholderKey ? copy.form[field.otherPlaceholderKey] : copy.form.other_country_placeholder}
          onChangeInput={(nextValue) => onChange(field.key, nextValue.target.value)}
          onBlurInput={() => {}}
        />
      )}
      {showHolderInput && (
        <Input
          id={`${field.key}_other`}
          name={`${field.key}_other`}
          type="text"
          colorGrey={true}
          defaultValue={isCustomHolderValue ? value : ""}
          placeholder={copy.form.holder_other_placeholder || field.placeholder}
          onChangeInput={(nextValue) => onChange(field.key, nextValue.target.value)}
          onBlurInput={() => {}}
        />
      )}
    </div>
  );
};

export default FieldControl;
